/**
 * features/finance/Vouchers.tsx
 * ------------------------------------------------------------------
 * The instruction to move money out of the school.
 *
 * A VOUCHER PASSES THROUGH THREE PAIRS OF HANDS: whoever raises it, whoever
 * checks it, whoever approves it. This screen shows a bursar the queue that
 * is actually theirs — what is waiting for them — rather than a list of
 * everything with most of the buttons greyed out.
 *
 * REFUSALS ARE SHOWN, NOT HIDDEN. Where an action is unavailable the reason
 * is printed: "you raised this voucher, so somebody else has to approve it".
 * A disabled button teaches nothing and gets clicked repeatedly; a sentence
 * ends the question. The sentence comes from the same pure module the Cloud
 * Function mirrors, so it reads identically whether the screen stopped
 * somebody or the server did.
 *
 * THE BANKING IS ON THE ROW for anything about to be paid. The control that
 * catches supplier fraud compares what the approver authorised against what
 * is about to leave, and the person releasing the payment should be able to
 * see the account they are paying into without opening anything.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import {
  Receipt, Plus, Check, ShieldCheck, Banknote, X, AlertTriangle,
  ShieldAlert, Clock, Trash2,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import Attachments from '@/features/finance/Attachments';
import {
  VOUCHER_COLLECTIONS, VOUCHER_STATUS_LABEL, VOUCHER_SOURCE_LABEL,
  PAYMENT_METHOD_OUT_LABEL, voucherActionProblem, availableActions,
  voucherStatusSummary, bankChangedSinceApproval, payablesAging,
  tradeCreditorsTotal,
  type Voucher, type VoucherActor, type VoucherAction, type VoucherStatus,
  type VoucherLine, type PaymentMethodOut,
} from '@/lib/vouchers';
import { SUPPLIER_COLLECTION, isBankVerified, type Supplier } from '@/lib/suppliers';
import { createVoucher, submitVoucher, runAction } from '@/features/finance/voucherWrite';
import { useGlData } from '@/features/finance/useGlData';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_TONE: Record<VoucherStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  submitted: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  checked: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  paid: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500',
};

const ACTION_LABEL: Record<VoucherAction, string> = {
  submit: 'Submit for checking',
  check: 'Check',
  approve: 'Approve',
  pay: 'Mark as paid',
  reject: 'Reject',
  cancel: 'Cancel',
};

type Filter = 'mine' | 'all' | VoucherStatus;

export default function Vouchers() {
  const { userData } = useAuth();
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);

  const can: VoucherActor = useMemo(() => ({
    uid: userData?.uid ?? '',
    name: userData?.displayName ?? userData?.email ?? '',
    canRaise: perms.canRaiseVouchers,
    canCheck: perms.canCheckVouchers,
    canApprove: perms.canApproveVouchers,
    canPay: perms.canReleasePayments,
  }), [userData, perms]);

  const actor = useMemo(() => ({
    uid: userData?.uid, email: userData?.email, displayName: userData?.displayName,
  }), [userData]);

  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('mine');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, VOUCHER_COLLECTIONS.vouchers), where('fy', '==', fy)),
      (snap) => {
        setVouchers(snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Voucher)
          .sort((a, b) => b.voucherNo.localeCompare(a.voucherNo)));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [fy]);

  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (snap) => setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);

  /* WHAT IS WAITING FOR THIS PERSON. A queue of everything with most of the
     buttons greyed out is a queue nobody works from. */
  const mine = useMemo(
    () => vouchers.filter((v) => availableActions(v, can).some(
      (a) => a === 'check' || a === 'approve' || a === 'pay' || (a === 'submit' && v.preparedBy === can.uid),
    )),
    [vouchers, can],
  );

  const shown = useMemo(() => {
    if (filter === 'mine') return mine;
    if (filter === 'all') return vouchers;
    return vouchers.filter((v) => v.currentStatus === filter);
  }, [vouchers, mine, filter]);

  const aging = useMemo(() => payablesAging(vouchers), [vouchers]);
  const owed = useMemo(() => tradeCreditorsTotal(vouchers), [vouchers]);
  const overdue = useMemo(
    () => aging.filter((b) => b.label !== 'Not yet due').reduce((a, b) => a + b.total, 0),
    [aging],
  );

  async function act(v: Voucher, action: VoucherAction) {
    const problem = voucherActionProblem(v, action, can);
    if (problem) { setMsg({ ok: false, text: problem }); return; }

    let reason: string | undefined;
    if (action === 'reject') {
      reason = window.prompt('Why is this being rejected? The preparer needs to know what to change.') ?? undefined;
      if (!reason) return;
    }
    if (action === 'cancel') {
      if (!window.confirm(`Cancel ${v.voucherNo}?`)) return;
    }
    let bankRef: string | undefined;
    if (action === 'pay') {
      bankRef = window.prompt('Bank reference for this payment (optional)') ?? undefined;
    }

    setBusy(v.id);
    setMsg(null);
    try {
      if (action === 'submit') {
        await submitVoucher(v.id, { ...actor, can });
        setMsg({ ok: true, text: `${v.voucherNo} sent for checking.` });
      } else {
        await runAction(action, v.id, { reason, bankRef });
        setMsg({ ok: true, text: `${v.voucherNo}: ${ACTION_LABEL[action].toLowerCase()} done.` });
      }
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  }

  if (loading || gl.loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  const years = Array.from({ length: 5 }, (_, i) => `FY${new Date().getFullYear() - i}`);
  const postable = gl.accounts.filter((a) => a.isPostable && a.active);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Receipt}
        title="Payment Vouchers"
        subtitle="Raised, checked and approved by three different people"
        actions={(
          <div className="flex items-center gap-2">
            <select value={fy} onChange={(e) => setFy(e.target.value)} className={glassFieldClass('py-1.5')}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {can.canRaise && (
              <button type="button" className={btnPrimary} onClick={() => setComposing(true)}>
                <Plus size={15} /> New voucher
              </button>
            )}
          </div>
        )}
      />

      {msg && (
        <div className={`rounded-xl px-4 py-3 text-sm flex items-start gap-2 ${msg.ok
          ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
          : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
        >
          {!msg.ok && <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatChip icon={Clock} label="Waiting for you" value={String(mine.length)} />
        <StatChip icon={Banknote} label="Owed to suppliers" value={money(owed)} />
        <StatChip icon={AlertTriangle} label="Past due" value={money(overdue)} />
        <StatChip icon={Receipt} label="Vouchers this year" value={String(vouchers.length)} />
      </div>

      <div className="flex flex-wrap gap-2">
        {([
          ['mine', `Waiting for you (${mine.length})`],
          ['submitted', 'Awaiting check'],
          ['checked', 'Awaiting approval'],
          ['approved', 'To pay'],
          ['all', `Everything (${vouchers.length})`],
        ] as [Filter, string][]).map(([f, label]) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={filter === f ? btnPrimary : btnGhost}
          >
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <SoftCard pad="p-8" hover={false}>
          <p className="text-sm text-center text-gray-500 dark:text-gray-400">
            {filter === 'mine' ? 'Nothing is waiting for you.' : 'Nothing here.'}
          </p>
        </SoftCard>
      )}

      {shown.map((v) => {
        const supplier = v.supplierId ? suppliers.find((s) => s.id === v.supplierId) : undefined;
        const tampered = bankChangedSinceApproval(v);
        const actions = availableActions(v, can);
        // The refusals worth SAYING — the ones a person would otherwise
        // click at repeatedly wondering why nothing happens.
        const refusals = (['check', 'approve', 'pay'] as VoucherAction[])
          .map((a) => ({ a, why: voucherActionProblem(v, a, can) }))
          .filter((x) => x.why && /you (raised|checked)|verified|changed after/i.test(x.why));

        return (
          <SoftCard key={v.id} pad="p-0" hover={false}>
            <PanelHeader
              icon={tampered ? AlertTriangle : Receipt}
              tone={tampered ? 'gold' : 'navy'}
              title={`${v.voucherNo} — ${v.payeeName}`}
              subtitle={voucherStatusSummary(v)}
              right={(
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_TONE[v.currentStatus]}`}>
                  {VOUCHER_STATUS_LABEL[v.currentStatus]}
                </span>
              )}
            />
            <div className="p-5 space-y-3">
              {tampered && (
                <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-1.5 rounded-lg bg-red-50 dark:bg-red-950/30 p-3">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    The bank details changed after this was approved
                    {v.payeeAtApproval?.bank?.accountNumber && v.payeeAtPayment?.bank?.accountNumber
                      ? ` — from ${v.payeeAtApproval.bank.accountNumber} to ${v.payeeAtPayment.bank.accountNumber}`
                      : ''}
                    . It cannot be paid until somebody re-approves it against the new details.
                  </span>
                </p>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Amount</p>
                  <p className="tabular-nums font-semibold text-gray-800 dark:text-gray-100">{money(v.total)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Method</p>
                  <p className="text-gray-700 dark:text-gray-200">{PAYMENT_METHOD_OUT_LABEL[v.method]}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Due</p>
                  <p className="text-gray-700 dark:text-gray-200">{v.dueDate ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Source</p>
                  <p className="text-gray-700 dark:text-gray-200">{VOUCHER_SOURCE_LABEL[v.source.type]}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300">{v.narration}</p>

              {/* The evidence behind the payment, where the person
                  checking or approving it is already looking. Asking them
                  to open another screen to see the invoice is how a
                  voucher gets approved without anybody reading it. */}
              <Attachments
                kind="voucher"
                recordId={v.id}
                recordRef={v.voucherNo}
                canRemove={perms.canApproveVouchers}
              />

              {/* ON THE ROW. Whoever releases the payment should see the
                  account without opening anything. */}
              {v.method === 'eft' && supplier && (
                <p className="text-sm flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                    {supplier.bank?.accountNumber ?? 'no account on file'}
                  </span>
                  {supplier.bank?.accountNumber && (
                    isBankVerified(supplier)
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300"><ShieldCheck size={12} /> verified</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300"><ShieldAlert size={12} /> not verified</span>
                  )}
                </p>
              )}

              <table className="w-full text-sm">
                <tbody>
                  {v.lines.map((l, i) => (
                    <tr key={`${l.accountCode}-${i}`} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-1.5 pr-3 font-mono text-xs text-gray-400 w-16">{l.accountCode}</td>
                      <td className="py-1.5 pr-3">{l.description}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* SAID, NOT GREYED OUT. */}
              {refusals.map(({ a, why }) => (
                <p key={a} className="text-xs text-gray-500 dark:text-gray-400">{why}</p>
              ))}

              <div className="flex flex-wrap justify-end gap-2">
                {actions.filter((a) => a !== 'cancel').map((a) => (
                  <button
                    key={a}
                    type="button"
                    className={a === 'reject' ? btnGhost : btnPrimary}
                    disabled={busy === v.id}
                    onClick={() => { void act(v, a); }}
                  >
                    {a === 'check' && <Check size={14} />}
                    {a === 'approve' && <ShieldCheck size={14} />}
                    {a === 'pay' && <Banknote size={14} />}
                    {a === 'reject' && <X size={14} />}
                    {ACTION_LABEL[a]}
                  </button>
                ))}
                {actions.includes('cancel') && (
                  <button type="button" className={btnGhost} disabled={busy === v.id} onClick={() => { void act(v, 'cancel'); }}>
                    <Trash2 size={14} /> Cancel
                  </button>
                )}
              </div>
            </div>
          </SoftCard>
        );
      })}

      {composing && (
        <NewVoucher
          fy={fy}
          suppliers={suppliers.filter((s) => s.active !== false)}
          accounts={postable.map((a) => ({ code: a.code, name: a.name }))}
          onClose={() => setComposing(false)}
          onDone={(text) => { setComposing(false); setMsg({ ok: true, text }); }}
          onError={(text) => setMsg({ ok: false, text })}
          actor={actor}
        />
      )}
    </div>
  );
}

/* ------------------------------ new voucher ------------------------------ */

const emptyLine = (): VoucherLine => ({ description: '', accountCode: '', amount: 0 });

function NewVoucher({
  fy, suppliers, accounts, onClose, onDone, onError, actor,
}: {
  fy: string;
  suppliers: Supplier[];
  accounts: { code: string; name: string }[];
  onClose: () => void;
  onDone: (text: string) => void;
  onError: (text: string) => void;
  actor: Parameters<typeof createVoucher>[1];
}) {
  const [supplierId, setSupplierId] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [narration, setNarration] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  const [method, setMethod] = useState<PaymentMethodOut>('eft');
  const [lines, setLines] = useState<VoucherLine[]>([emptyLine()]);
  const [busy, setBusy] = useState(false);

  const total = lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const supplier = suppliers.find((s) => s.id === supplierId);

  async function save() {
    setBusy(true);
    try {
      const v = await createVoucher({
        fy,
        source: { type: supplierId ? 'supplier_invoice' : 'adhoc' },
        payeeName: supplier?.name ?? payeeName,
        ...(supplierId ? { supplierId } : {}),
        lines: lines.filter((l) => Number(l.amount) !== 0),
        narration,
        ...(dueDate ? { dueDate } : {}),
        paymentRef,
        supplierInvoiceNo,
        method,
      }, actor);
      onDone(`${v.voucherNo} drafted. Submit it when you are ready for it to be checked.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-[2px]" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">New payment voucher</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Supplier</label>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={glassFieldClass('py-2')}>
                <option value="">Somebody not on the register</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {!supplierId && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Payee</label>
                <input value={payeeName} onChange={(e) => setPayeeName(e.target.value)} className={glassFieldClass('py-2')} />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethodOut)} className={glassFieldClass('py-2')}>
                {(Object.keys(PAYMENT_METHOD_OUT_LABEL) as PaymentMethodOut[]).map((m) => (
                  <option key={m} value={m}>{PAYMENT_METHOD_OUT_LABEL[m]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Due date</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={glassFieldClass('py-2')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Reference the payee will see
              </label>
              <input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} className={glassFieldClass('py-2')} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">What is this for?</label>
              <input value={narration} onChange={(e) => setNarration(e.target.value)} className={glassFieldClass('py-2')} />
            </div>
            <div>
              {/* THEIR number, not ours. It is what goes on the remittance
                  advice so they can match the payment, and it is the strongest
                  duplicate check the system has — the same invoice number twice
                  is a duplicate, where the same amount twice is often a
                  standing weekly charge. */}
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Supplier&rsquo;s invoice number
              </label>
              <input
                value={supplierInvoiceNo}
                onChange={(e) => setSupplierInvoiceNo(e.target.value)}
                placeholder="As printed on their invoice"
                className={glassFieldClass('py-2')}
              />
            </div>
          </div>

          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input
                  value={l.description}
                  placeholder="Description"
                  onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                  className={glassFieldClass('py-2 col-span-5')}
                />
                <select
                  value={l.accountCode}
                  onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, accountCode: e.target.value } : x)))}
                  className={glassFieldClass('py-2 col-span-4')}
                >
                  <option value="">Account…</option>
                  {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                </select>
                <input
                  value={String(l.amount || '')}
                  inputMode="decimal"
                  placeholder="Amount"
                  onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: Number(e.target.value) || 0 } : x)))}
                  className={glassFieldClass('py-2 col-span-3 text-right')}
                />
              </div>
            ))}
            <button type="button" className={btnGhost} onClick={() => setLines([...lines, emptyLine()])}>
              <Plus size={14} /> Add a line
            </button>
          </div>

          <p className="text-sm text-right font-semibold text-gray-800 dark:text-gray-100">
            Total {money(total)}
          </p>

          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
            <button type="button" className={btnPrimary} disabled={busy} onClick={() => { void save(); }}>
              {busy ? 'Saving…' : 'Save as draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
